import { TriangulationResult } from '../types';

/** Odległość kwadratowa od punktu (px,py) do odcinka (x1,y1)-(x2,y2). */
function distSqToSeg(
  px: number, py: number,
  x1: number, y1: number, x2: number, y2: number,
): number {
  const dx = x2 - x1, dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-12) {
    return (px - x1) ** 2 + (py - y1) ** 2;
  }
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const fx = px - (x1 + t * dx);
  const fy = py - (y1 + t * dy);
  return fx * fx + fy * fy;
}

/**
 * Szacuje maksymalną sensowną wysokość wydmuchu na podstawie triangulacji.
 *
 * Algorytm: znajdź największe koło wpisane we wszystkie otwory szablonu
 * (krawędzie graniczne między obszarem otworu a materiałem).
 * Zwraca promień tego koła w mm – to propozycja wartości „Max height".
 *
 * Sprawdza zarówno wierzchołki, jak i centroidy trójkątów otworów,
 * żeby zminimalizować błąd wynikający z gęstości siatki.
 */
export function estimateMaxBlowHeight(tri: TriangulationResult): number {
  const { vertices, triangles, triLabels, nodeRegions } = tri;

  // ── Krawędzie graniczne: sąsiadujące z otworem (label>0) i z materiałem
  //    (label=0) lub krawędzią siatki (tylko jeden sąsiad). ──────────────
  const edgeAdj = new Map<string, number[]>(); // klucz → [label sąsiada 1, ...]
  for (let t = 0; t < triangles.length; t++) {
    const [a, b, c] = triangles[t];
    const label = triLabels[t];
    for (const [u, v] of [[a, b], [b, c], [c, a]] as [number, number][]) {
      const key = u < v ? `${u}_${v}` : `${v}_${u}`;
      if (!edgeAdj.has(key)) edgeAdj.set(key, []);
      edgeAdj.get(key)!.push(label);
    }
  }

  // [x1,y1,x2,y2] krawędzi granicznych
  const boundary: [number, number, number, number][] = [];
  for (const [key, labels] of Array.from(edgeAdj)) {
    const hasOpening  = labels.some(l => l > 0);
    const hasMaterial = labels.some(l => l === 0) || labels.length === 1;
    if (hasOpening && hasMaterial) {
      const [u, v] = key.split('_').map(Number);
      boundary.push([vertices[u].x, vertices[u].y, vertices[v].x, vertices[v].y]);
    }
  }

  if (boundary.length === 0) return 0;

  let maxDistSq = 0;

  const tryPoint = (px: number, py: number) => {
    let minSq = Infinity;
    for (const [x1, y1, x2, y2] of boundary) {
      const d = distSqToSeg(px, py, x1, y1, x2, y2);
      if (d < minSq) minSq = d;
    }
    if (minSq > maxDistSq) maxDistSq = minSq;
  };

  // Wierzchołki wewnątrz otworów
  for (let i = 0; i < vertices.length; i++) {
    if (nodeRegions[i] === 0) continue;
    tryPoint(vertices[i].x, vertices[i].y);
  }

  // Centroidy trójkątów otworów (dodatkowe punkty kandydujące)
  for (let t = 0; t < triangles.length; t++) {
    if (triLabels[t] === 0) continue;
    const [a, b, c] = triangles[t];
    tryPoint(
      (vertices[a].x + vertices[b].x + vertices[c].x) / 3,
      (vertices[a].y + vertices[b].y + vertices[c].y) / 3,
    );
  }

  return Math.sqrt(maxDistSq); // [mm]
}
